import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AtSign, Download, Loader2, Save, Sparkles } from 'lucide-react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/ui/Button';
import ToastContainer from '../../components/ui/ToastContainer';
import BudgetGrid from '../../components/assistants/BudgetGrid';
import SelecteurProjet from '../../components/assistants/SelecteurProjet';
import RenduDocument from '../../components/assistants/RenduDocument';
import ApercuPresentation from '../../components/assistants/ApercuPresentation';
import ApercuDocx from '../../components/assistants/ApercuDocx';
import RenduCarnet from '../../components/assistants/RenduCarnet';
import RenduAudio from '../../components/assistants/RenduAudio';
import RenduVideo from '../../components/assistants/RenduVideo';
import RenduVisuel from '../../components/assistants/RenduVisuel';
import TitreConversation from '../../components/assistants/TitreConversation';
import ApercuTableur from '../../components/assistants/ApercuTableur';
import QuestionsCadrage from '../../components/assistants/QuestionsCadrage';
import Reorientation from '../../components/assistants/Reorientation';
import Composeur from '../../components/conversation/Composeur';
import { type Agent, useAgents } from '../../hooks/useAgents';
import type { DemarrageConversation } from './ConversationsPage';
import { useToast } from '../../hooks/useToast';
import { authManager } from '../../services/authManager';
import {
  assistantsService,
  type AssistantMessage,
  type Budget,
  type ConversationDetail,
} from '../../services/api/assistants';

/**
 * Page de conversation d'un cas d'usage intégré.
 *
 * L'échange se déroule entièrement dans OmnIA-EG : aucun onglet externe,
 * aucun compte tiers. La production est rendue nativement, corrigée sur place,
 * et le fichier n'est généré qu'au téléchargement.
 */
const AssistantPage: React.FC = () => {
  // Deux entrées : par l'assistant, qui ouvre un échange neuf ; ou par une
  // conversation précise, que l'on reprend là où elle s'était arrêtée.
  const { templateId, conversationId } = useParams<{
    templateId?: string;
    conversationId?: string;
  }>();
  const { toasts, removeToast, showError, showInfo } = useToast();

  const [conversation, setConversation] = useState<ConversationDetail | null>(null);
  const [saisie, setSaisie] = useState('');
  const [chargement, setChargement] = useState(true);
  const [generation, setGeneration] = useState(false);
  const [enregistrement, setEnregistrement] = useState(false);
  const [brouillons, setBrouillons] = useState<Record<number, Budget>>({});
  // Vue choisie par message : lecture à l'écran ou aperçu du fichier Word.
  const [vues, setVues] = useState<Record<number, 'lecture' | 'document'>>({});
  // Suivi de la génération en cours : étape annoncée et texte déjà reçu.
  const [etape, setEtape] = useState('');
  const [texteEnCours, setTexteEnCours] = useState('');
  // Gamma met une trentaine de secondes : le bouton doit le dire.
  const [gamma, setGamma] = useState<number | null>(null);
  const finDeListe = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { agents } = useAgents();
  // Agent désigné par « @ » pour la suite : s'il diffère de celui de la
  // conversation, le message ouvre un nouvel échange avec lui.
  const [agentCible, setAgentCible] = useState<Agent | null>(null);
  // Le premier message arrive de l'accueil : on garde comment l'agent a été
  // choisi, pour le dire, et on veille à ne l'envoyer qu'une fois.
  const demarrage = useRef<DemarrageConversation | null>(
    (location.state as DemarrageConversation | null) || null,
  );
  const [choix, setChoix] = useState<{ automatique: boolean; raison: string } | null>(
    demarrage.current
      ? { automatique: demarrage.current.automatique, raison: demarrage.current.raison }
      : null,
  );
  const premierEnvoye = useRef(false);

  useEffect(() => {
    const initialiser = async () => {
      try {
        // Reprendre un échange existant plutôt qu'en ouvrir un vide : sans
        // cela, l'historique est conservé mais reste hors d'atteinte.
        const identifiant = conversationId
          ? Number(conversationId)
          : (await assistantsService.ouvrirConversation(Number(templateId))).id;
        setConversation(await assistantsService.chargerConversation(identifiant));
      } catch (erreur: any) {
        showError('Ouverture impossible', erreur.message);
      } finally {
        setChargement(false);
      }
    };
    initialiser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, conversationId]);

  useEffect(() => {
    finDeListe.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation?.messages.length, generation]);

  const envoyer = useCallback(async (texte?: string, sansQuestions = false) => {
    // Le premier argument n'est retenu que si c'est bien du texte. Branchée
    // directement sur un bouton, cette fonction recevait l'événement de clic :
    // « .trim() » sur un événement lève, la levée reste dans le gestionnaire,
    // et l'agent voyait un bouton sans effet, sans message et sans erreur.
    const contenu = (typeof texte === 'string' ? texte : saisie).trim();
    if (!contenu || !conversation || generation) return;

    setSaisie('');
    setGeneration(true);
    setEtape('');
    setTexteEnCours('');

    // Affichage optimiste du message de l'utilisateur
    const provisoire: AssistantMessage = {
      id: -Date.now(),
      role: 'user',
      content: contenu,
      payload: null,
      created_at: new Date().toISOString(),
      usage: { input_tokens: 0, output_tokens: 0, cache_read_tokens: 0, retries: 0 },
    };
    setConversation({ ...conversation, messages: [...conversation.messages, provisoire] });

    try {
      const reponse = await assistantsService.envoyerMessageEnFlux(
        conversation.id,
        contenu,
        (evenement) => {
          if (evenement.type === 'etape') setEtape(evenement.libelle);
          if (evenement.type === 'texte') setTexteEnCours((t) => t + evenement.delta);
          if (evenement.type === 'reprise') {
            // Les contrôles ont rejeté la sortie : on repart d'une page
            // blanche plutôt que d'empiler deux versions.
            setTexteEnCours('');
            setEtape('Incohérence détectée — correction en cours');
          }
        },
        sansQuestions,
      );
      const rafraichie = await assistantsService.chargerConversation(conversation.id);
      setConversation(rafraichie);
      if (reponse.usage.retries > 0) {
        showInfo(
          'Production corrigée automatiquement',
          `${reponse.usage.retries} relance(s) ont été nécessaires pour obtenir un résultat cohérent.`,
        );
      }
    } catch (erreur: any) {
      showError('Génération impossible', erreur.message);
      setConversation(conversation);
      setSaisie(contenu);
    } finally {
      setGeneration(false);
      setEtape('');
      setTexteEnCours('');
    }
  }, [saisie, conversation, generation, showError, showInfo]);

  // Envoi du premier message, écrit sur l'accueil. L'état de navigation est
  // effacé aussitôt : un rechargement de la page ne doit pas le renvoyer.
  useEffect(() => {
    const premier = demarrage.current?.premierMessage;
    if (!conversation || !premier || premierEnvoye.current) return;
    if (conversation.messages.length > 0) return;
    premierEnvoye.current = true;
    window.history.replaceState({}, '');
    envoyer(premier);
  }, [conversation, envoyer]);

  // Nouvelle adresse, nouvelle conversation : on repart d'un état propre.
  useEffect(() => {
    demarrage.current = (location.state as DemarrageConversation | null) || null;
    premierEnvoye.current = false;
    dejaRelaye.current = false;
    setAgentCible(null);
    setChoix(
      demarrage.current
        ? { automatique: demarrage.current.automatique, raison: demarrage.current.raison }
        : null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, templateId]);

  /**
   * Envoie la saisie : à l'agent de la conversation, ou à un autre agent
   * désigné par « @ », qui reprend alors la demande dans un nouvel échange.
   */
  const soumettre = async () => {
    const contenu = saisie.trim();
    if (!contenu || !conversation || generation) return;
    if (!agentCible || agentCible.id === conversation.template) {
      setAgentCible(null);
      envoyer(contenu);
      return;
    }
    setGeneration(true);
    try {
      const ouverte = await assistantsService.demarrerConversation(
        contenu, agentCible.id, conversation.project ?? null,
      );
      const etat: DemarrageConversation = { premierMessage: contenu, automatique: false, raison: '' };
      setSaisie('');
      setAgentCible(null);
      navigate(`/workspace/conversation/${ouverte.id}`, { state: etat });
    } catch (erreur: any) {
      showError("Changement d'agent impossible", erreur.message);
    } finally {
      setGeneration(false);
    }
  };

  // Claude, l'assistant général : la dernière issue quand une demande ne
  // relève d'aucun assistant spécialisé.
  const assistantGeneral = agents.find((a) => a.category_code === 'general') || null;

  const versGeneral = async (demande: string, automatique = false) => {
    if (!assistantGeneral || !demande.trim()) return;
    try {
      const ouverte = await assistantsService.demarrerConversation(
        demande, assistantGeneral.id, conversation?.project ?? null,
      );
      const etat: DemarrageConversation = {
        premierMessage: demande,
        automatique,
        raison: automatique ? "Aucun assistant spécialisé ne couvre cette demande." : '',
      };
      navigate(`/workspace/conversation/${ouverte.id}`, { state: etat, replace: automatique });
    } catch (erreur: any) {
      showError("Transmission à Claude impossible", erreur.message);
    }
  };

  // Le premier message, choisi automatiquement, est jugé hors du périmètre de
  // l'assistant retenu : plutôt que de laisser l'agent devant un refus, on
  // confie la demande à Claude, sans qu'il ait à cliquer.
  const dejaRelaye = useRef(false);
  useEffect(() => {
    if (!conversation || !choix?.automatique || dejaRelaye.current || !assistantGeneral) return;
    if (conversation.template === assistantGeneral.id) return;
    const [premier, reponse] = conversation.messages;
    if (conversation.messages.length === 2 && premier?.role === 'user' && reponse?.payload?.hors_perimetre) {
      dejaRelaye.current = true;
      versGeneral(premier.content, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation, choix, assistantGeneral]);

  const enregistrer = async (message: AssistantMessage) => {
    const brouillon = brouillons[message.id];
    if (!conversation || !brouillon) return;

    setEnregistrement(true);
    try {
      await assistantsService.enregistrerCorrections(conversation.id, message.id, brouillon);
      setBrouillons((precedent) => {
        const suivant = { ...precedent };
        delete suivant[message.id];
        return suivant;
      });
      setConversation(await assistantsService.chargerConversation(conversation.id));
      showInfo('Corrections enregistrées', "Elles seront reprises dans l'export.");
    } catch (erreur: any) {
      showError('Enregistrement impossible', erreur.message);
    } finally {
      setEnregistrement(false);
    }
  };

  const exporter = async (message: AssistantMessage, moteur?: 'gamma', fichier?: 'budget') => {
    if (!conversation) return;
    if (brouillons[message.id]) {
      showInfo('Corrections non enregistrées', 'Enregistrez-les avant d\'exporter.');
      return;
    }
    try {
      await assistantsService.exporter(
        conversation.id,
        message.id,
        message.payload?.titre || 'budget',
        moteur,
        fichier,
      );
    } catch (erreur: any) {
      showError('Export impossible', erreur.message);
    }
  };

  if (chargement) {
    return (
      <DashboardLayout>
        <div className="flex h-96 items-center justify-center text-[13px] text-eg-gris">
          <Loader2 className="mr-2.5 h-4 w-4 animate-spin" />
          Ouverture de la conversation…
        </div>
      </DashboardLayout>
    );
  }

  const estTableau = conversation?.output_format === 'tableau';

  // Initiales de l'auteur, en tête de ses propres messages : dans un échange
  // rattaché à un dossier d'équipe, il faut voir qui a demandé quoi.
  const compte = authManager.getUser();
  const initialesUtilisateur =
    (compte?.full_name || compte?.email || 'Vous')
      .split(/[\s.@]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((mot: string) => mot[0]?.toUpperCase())
      .join('') || 'MOI';

  // Les invites étaient écrites pour la budgétisation et s'affichaient sur
  // tous les assistants. Chaque format appelle sa propre consigne.
  const INVITES: Record<string, { amorce: string; exemple: string }> = {
    tdr: {
      amorce: "Décrivez l'activité : objet, lieu, durée, participants.",
      exemple: "TDR d'une formation de 3 jours à Kindia pour 50 agents de santé sur la sensibilisation à la vaccination de routine.",
    },
    tableau: {
      amorce: "Décrivez l'activité à chiffrer.",
      exemple: "Budget d'un atelier de formation MLM de 3 jours à Kankan, 25 participants venus de 5 préfectures.",
    },
    audio: {
      amorce: 'Décrivez le message à porter et sa durée.',
      exemple: "Spot radio de 30 secondes sur la vaccination de routine, pour les radios communautaires de Kankan.",
    },
    image: {
      amorce: 'Décrivez le visuel et son public.',
      exemple: "Affiche pour la campagne de rattrapage vaccinal à Siguiri, destinée aux mères, format portrait.",
    },
    carnet: {
      amorce: "Posez votre question sur les documents déposés dans l'activité.",
      exemple: "Quels districts ont signalé une rupture de BCG au deuxième trimestre ?",
    },
    presentation: {
      amorce: 'Décrivez la présentation attendue.',
      exemple: "Restitution trimestrielle pour la région de Kankan, une dizaine de diapositives.",
    },
    // Les deux formats vidéo n'avaient pas d'entrée et retombaient sur
    // l'exemple générique, qui propose une note écrite d'une page : on demandait
    // donc une capsule de sensibilisation en montrant l'exemple d'un document.
    capsule: {
      amorce: 'Décrivez le message de sensibilisation, son public et sa durée.',
      exemple: "Capsule de 30 secondes pour convaincre les mères de Siguiri de faire vacciner leur enfant avant un an, pour les réseaux sociaux et les séances communautaires.",
    },
    restitution: {
      amorce: 'Décrivez la restitution à commenter et la période couverte.',
      exemple: "Restitution commentée des couvertures vaccinales du deuxième trimestre 2026 pour la région de Kankan, à regarder sur téléphone.",
    },
  };
  const invite = INVITES[conversation?.output_format || ''] || {
    amorce: 'Décrivez ce que vous attendez.',
    exemple: "Note de synthèse d'une page sur les ruptures de vaccins observées ce trimestre.",
  };

  return (
    <DashboardLayout pleinCadre>
      <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto max-w-[900px] space-y-6 px-4 pb-10 pt-5 lg:px-8">
        <div className="flex items-start gap-3 border-b border-eg-bordure pb-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 text-[12px] text-eg-gris">
              <span className="inline-flex items-center gap-1 rounded-full bg-eg-accent/[0.1] px-2 py-0.5
                font-medium text-eg-accent">
                <AtSign className="h-3 w-3" />
                {conversation?.template_title || 'Agent'}
              </span>
              {choix?.automatique && (
                <span className="inline-flex items-center gap-1 text-eg-estompe" title={choix.raison}>
                  <Sparkles className="h-3 w-3" />
                  choisi d'après votre message
                </span>
              )}
              {conversation?.project_name && (
                <span className="text-eg-estompe">· {conversation.project_name}</span>
              )}
            </div>
            {conversation && (
              <div className="mt-0.5">
                <TitreConversation
                  conversationId={conversation.id}
                  titre={conversation.title}
                  onRenomme={(titre) =>
                    setConversation((precedente) =>
                      precedente ? { ...precedente, title: titre } : precedente,
                    )
                  }
                />
              </div>
            )}
          </div>
          {conversation && (
            <SelecteurProjet
              conversationId={conversation.id}
              projetActuel={conversation.project ?? null}
              templateId={conversation.template}
              aDesMessages={conversation.messages.length > 0}
            />
          )}
        </div>

        {/* Aucune conversation d'Efficience Globale n'était rattachée à une
            activité. Or c'est elle qui porte les documents citables : sans
            elle, on répond à trois questions de précision pour s'entendre dire
            qu'il n'y a aucune source. L'avertissement passe donc avant la
            saisie, pas après la production. */}
        {conversation && !conversation.project && conversation.messages.length === 0
          && conversation.output_format !== 'texte' && (
          <div className={`rounded-md border px-5 py-4 ${
            conversation.output_format === 'carnet'
              ? 'border-eg-alerte/40 bg-eg-alerte/10'
              : 'border-eg-bord bg-eg-papier'
          }`}>
            <p className="text-[13px] font-semibold text-eg-encre">
              Cet échange n'est rattaché à aucune activité.
            </p>
            <p className="mt-1 text-[12.5px] leading-relaxed text-eg-gris">
              {conversation.output_format === 'carnet'
                ? "L'assistant ne pourra citer aucun document : les pièces "
                  + "interrogeables sont celles déposées dans une activité. "
                  + "Choisissez-en une ci-dessus avant de poser votre question."
                : "L'assistant travaillera sans les documents de votre activité. "
                  + "Rattachez-en une ci-dessus s'ils sont utiles à cette production."}
            </p>
          </div>
        )}

        {conversation?.messages.length === 0 && !demarrage.current?.premierMessage && (
          <div className="rounded-2xl bg-eg-papier px-5 py-5 text-eg-gris">
            <p className="mb-1 text-eg-encre">{invite.amorce}</p>
            <p className="text-[13px]">Par exemple : « {invite.exemple} »</p>
          </div>
        )}

        {conversation?.messages.map((message, rang) => {
          const budget: Budget | null =
            estTableau && message.payload ? brouillons[message.id] ?? message.payload : null;
          const modifie = Boolean(brouillons[message.id]);

          if (message.role === 'user') {
            return (
              <div key={message.id} className="flex gap-2.5">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full
                  bg-eg-voile text-[10px] font-semibold text-eg-brun">
                  {initialesUtilisateur}
                </div>
                <div className="whitespace-pre-wrap rounded-md border border-eg-bordure
                  bg-eg-blanc px-3 py-2.5 text-[13px] text-[#1F2D35]">
                  {message.content}
                </div>
              </div>
            );
          }

          return (
            <div key={message.id} className="flex gap-2.5">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[5px]
                bg-eg-ombre text-[10px] font-semibold text-eg-blanc">
                EG
              </div>
              <div className="min-w-0 flex-1 space-y-4 rounded-md border border-[#DDE2DD]
                bg-eg-blanc p-4">
              {budget ? (
                <>
                  <div className="flex gap-1 border-b border-eg-bordure pb-3">
                    {(['lecture', 'document'] as const).map((vue) => (
                      <button
                        key={vue}
                        type="button"
                        onClick={() => setVues((v) => ({ ...v, [message.id]: vue }))}
                        className={`rounded-md px-3 py-1.5 text-[12.5px] transition-colors ${
                          (vues[message.id] || 'lecture') === vue
                            ? 'bg-eg-ombre text-eg-blanc'
                            : 'text-eg-gris hover:bg-eg-voile'
                        }`}
                      >
                        {vue === 'lecture' ? 'Grille corrigeable' : 'Aperçu Excel'}
                      </button>
                    ))}
                  </div>

                  {(vues[message.id] || 'lecture') === 'lecture' ? (
                    <BudgetGrid
                      budget={budget}
                      onChange={(suivant) =>
                        setBrouillons((precedent) => ({ ...precedent, [message.id]: suivant }))
                      }
                    />
                  ) : (
                    <ApercuTableur budget={budget} />
                  )}
                  <div className="flex items-center justify-between border-t border-eg-bordure pt-4">
                    <p className="text-xs text-eg-estompe">
                      {message.usage.input_tokens + message.usage.output_tokens} jetons
                      {message.usage.retries > 0 && ` · ${message.usage.retries} relance(s)`}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!modifie}
                        loading={enregistrement}
                        icon={<Save className="w-4 h-4" />}
                        onClick={() => enregistrer(message)}
                      >
                        Enregistrer les corrections
                      </Button>
                      <Button
                        size="sm"
                        icon={<Download className="w-4 h-4" />}
                        onClick={() => exporter(message)}
                      >
                        Exporter en .xlsx
                      </Button>
                    </div>
                  </div>
                </>
              ) : message.payload?.hors_perimetre ? (
                <Reorientation
                  onGeneral={
                    assistantGeneral && assistantGeneral.id !== conversation?.template
                      ? () => versGeneral(conversation?.messages[rang - 1]?.content || '')
                      : undefined
                  }
                  motif={message.payload.motif || message.payload.comprehension}
                  suggestions={message.payload.suggestions || []}
                />
              ) : message.payload?.questions ? (
                <QuestionsCadrage
                  comprehension={message.payload.comprehension}
                  questions={message.payload.questions}
                  occupe={generation}
                  onRepondre={(reponse, forcer) => envoyer(reponse, forcer)}
                />
              ) : conversation?.output_format === 'restitution'
                  || conversation?.output_format === 'capsule' ? (
                // Vidéo. Le format est lu sur la conversation et non deviné du
                // contenu : une restitution et une présentation ont la même
                // forme de plans, seul l'assistant les distingue.
                <>
                  <RenduVideo
                    restitution={
                      conversation.output_format === 'restitution'
                        ? message.payload
                        : undefined
                    }
                    capsule={
                      conversation.output_format === 'capsule'
                        ? message.payload
                        : undefined
                    }
                    conversationId={conversation.id}
                    messageId={message.id}
                  />
                  <p className="border-t border-eg-bordure pt-4 text-xs text-eg-estompe">
                    {message.usage.input_tokens + message.usage.output_tokens} jetons
                    {message.usage.retries > 0 &&
                      ` · ${message.usage.retries} relance(s) sur le découpage`}
                  </p>
                </>
              ) : message.payload?.invite ? (
                // Visuel : la consigne se relit et se retouche avant exécution.
                <>
                  <RenduVisuel
                    consigne={message.payload}
                    conversationId={conversation!.id}
                    messageId={message.id}
                  />
                  <p className="border-t border-eg-bordure pt-4 text-xs text-eg-estompe">
                    {message.usage.input_tokens + message.usage.output_tokens} jetons
                    {message.usage.retries > 0 &&
                      ` · ${message.usage.retries} relance(s) sur la consigne`}
                  </p>
                </>
              ) : message.payload?.segments ? (
                // Contenu sonore : chaque réplique s'écoute et se retouche.
                <>
                  <RenduAudio
                    contenu={message.payload}
                    conversationId={conversation!.id}
                    messageId={message.id}
                  />
                  <p className="border-t border-eg-bordure pt-4 text-xs text-eg-estompe">
                    {message.usage.input_tokens + message.usage.output_tokens} jetons
                    {message.usage.retries > 0 &&
                      ` · ${message.usage.retries} relance(s) pour tenir la durée`}
                  </p>
                </>
              ) : message.payload?.citations || message.payload?.hors_corpus ? (
                // Interrogation de documents : la réponse vient avec ses pièces.
                <>
                  <RenduCarnet
                    reponse={message.payload.reponse}
                    citations={message.payload.citations || []}
                    horsCorpus={message.payload.hors_corpus}
                  />
                  <p className="border-t border-eg-bordure pt-4 text-xs text-eg-estompe">
                    {message.usage.input_tokens + message.usage.output_tokens} jetons
                    {message.usage.retries > 0 &&
                      ` · ${message.usage.retries} relance(s) pour obtenir des citations exactes`}
                  </p>
                </>
              ) : message.payload?.contenu ? (
                // Format « document » : Markdown structuré à rendre, pas à afficher brut.
                <>
                  {/* Deux vues : le contenu à l'écran, ou le fichier tel qu'il
                      sera ouvert dans Word. */}
                  <div className="flex gap-1 border-b border-eg-bordure pb-3">
                    {(['lecture', 'document'] as const).map((vue) => (
                      <button
                        key={vue}
                        type="button"
                        onClick={() => setVues((v) => ({ ...v, [message.id]: vue }))}
                        className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                          (vues[message.id] || 'lecture') === vue
                            ? 'bg-eg-ombre text-eg-blanc'
                            : 'text-eg-gris hover:bg-eg-voile'
                        }`}
                      >
                        {vue === 'lecture' ? 'Lecture' : 'Aperçu Word'}
                      </button>
                    ))}
                  </div>

                  {(vues[message.id] || 'lecture') === 'lecture' ? (
                    <RenduDocument titre={message.payload.titre} contenu={message.payload.contenu} />
                  ) : (
                    <ApercuDocx conversationId={conversation!.id} messageId={message.id} />
                  )}

                  <div className="flex items-center justify-between border-t border-eg-bordure pt-4">
                    <p className="text-xs text-eg-estompe">
                      {message.usage.input_tokens + message.usage.output_tokens} jetons
                    </p>
                    {/* Un TDR sort en deux fichiers : le TDR en Word et son
                        budget en Excel, qu'il cite. */}
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        icon={<Download className="w-4 h-4" />}
                        onClick={() => exporter(message)}
                      >
                        {message.payload.budget ? 'TDR en .docx' : 'Télécharger en .docx'}
                      </Button>
                      {message.payload.budget && (
                        <Button
                          size="sm"
                          variant="outline"
                          icon={<Download className="w-4 h-4" />}
                          onClick={() => exporter(message, undefined, 'budget')}
                        >
                          Budget en .xlsx
                        </Button>
                      )}
                    </div>
                  </div>
                </>
              ) : message.payload?.slides ? (
                <>
                  <ApercuPresentation
                    titre={message.payload.titre}
                    slides={message.payload.slides}
                    conversationId={conversation!.id}
                    messageId={message.id}
                  />
                  <div className="flex items-center justify-between border-t border-eg-bordure pt-4">
                    <p className="text-xs text-eg-estompe">
                      {message.payload.slides.length} diapositive
                      {message.payload.slides.length > 1 ? 's' : ''}
                      {' · '}
                      {message.usage.input_tokens + message.usage.output_tokens} jetons
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      {/* Deux mises en forme du même contenu : la charte du
                          programme, ou l'habillage de Gamma. */}
                      <Button
                        size="sm"
                        icon={<Download className="w-4 h-4" />}
                        onClick={() => exporter(message)}
                      >
                        .pptx — charte PEV
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        loading={gamma === message.id}
                        icon={<Sparkles className="w-4 h-4" />}
                        onClick={async () => {
                          setGamma(message.id);
                          await exporter(message, 'gamma');
                          setGamma(null);
                        }}
                      >
                        .pptx — mise en forme Gamma
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                /* Les assistants ont pour consigne d'ecrire en Markdown, et
                   cette branche l'affichait tel quel : un brainstorming sortait
                   avec ses « ## », ses « ** » et ses traits de separation en
                   clair. Seul le format document passait par le rendu. */
                <div className="text-[13px] text-[#1F2D35]">
                  <RenduDocument contenu={message.content} />
                </div>
              )}
              </div>
            </div>
          );
        })}

        {generation && (
          <div className="flex gap-2.5">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[5px]
              bg-eg-ombre text-[10px] font-semibold text-eg-blanc">
              EG
            </div>
            <div className="min-w-0 flex-1 space-y-3">
              {/* L'étape reste affichée même quand le texte arrive : elle dit ce
                  qui se passe, le texte dit seulement ce qui est déjà écrit. */}
              <div className="flex items-center gap-3 rounded-md border border-[#C9D0C9]
                bg-[#EEF2ED] px-3 py-2.5">
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-eg-accent" strokeWidth={2.6} />
                <span className="text-[12px] font-semibold text-eg-ombre">
                  {etape || 'Prise en compte de votre demande'}
                </span>
                <span className="h-[3px] flex-1 overflow-hidden rounded-sm bg-[#EEF2ED]">
                  <span className="block h-full w-1/3 animate-pulse rounded-sm bg-eg-accent" />
                </span>
              </div>

              {texteEnCours && (
                <div className="rounded-md border border-[#DDE2DD] bg-eg-blanc p-4">
                  <RenduDocument contenu={texteEnCours} />
                  <span className="ml-0.5 inline-block animate-pulse align-middle font-bold
                    text-eg-accent">▌</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div ref={finDeListe} />
      </div>

      </div>

      <div className="shrink-0 px-4 pb-3 pt-2 lg:px-8">
        <div className="mx-auto max-w-[900px]">
          <Composeur
            agents={agents}
            agent={agentCible}
            onAgent={setAgentCible}
            valeur={saisie}
            onChange={setSaisie}
            onEnvoyer={soumettre}
            occupe={generation}
            placeholder={`${invite.amorce} Tapez @ pour passer à un autre agent.`}
            mentionAuto={`Réponse par ${conversation?.template_title || "l'agent de la conversation"}`}
          />
        </div>
      </div>

      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </DashboardLayout>
  );
};

export default AssistantPage;
